<template>
  <modal :show.sync="show" :on-close="handleCloseWithGuard" size="medium">
    <div class="column content-box">
      <woot-modal-header
        :header-title="$t('CRM_MGMT.ADD.TITLE')"
        :header-content="$t('CRM_MGMT.ADD.DESC')"
      />
      <form class="row" @submit.prevent="createKanban()">
        <div class="medium-12 columns">
          <label :class="{ error: $v.name.$error }">
            {{ $t('CRM_MGMT.FORM.NAME.LABEL') }}
            <input
              v-model.trim="name"
              type="text"
              :placeholder="$t('CRM_MGMT.FORM.NAME.PLACEHOLDER')"
              @input="$v.name.$touch"
            />
          </label>
        </div>

        <div class="medium-12 columns">
          <label :class="{ error: $v.description.$error }">
            {{ $t('CRM_MGMT.FORM.DESCRIPTION.LABEL') }}
            <input
              v-model.trim="description"
              type="text"
              :placeholder="$t('CRM_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
              @input="$v.description.$touch"
            />
          </label>
        </div>

          <div class="medium-12 columns">
            <label>
              {{ $t('CRM_MGMT.FORM.TYPE_OPPORTUNITY.LABEL') || 'Type Opportunity' }}
              <select v-model="typeOpportunity">
                <option value="tickets">{{ $t('CRM_MGMT.FORM.TYPE_OPPORTUNITY.OPTIONS.TICKETS') }}</option>
                <option value="business">{{ $t('CRM_MGMT.FORM.TYPE_OPPORTUNITY.OPTIONS.BUSINESS') }}</option>
                <option value="both">{{ $t('CRM_MGMT.FORM.TYPE_OPPORTUNITY.OPTIONS.BOTH') }}</option>
              </select>
            </label>
          </div>

        <div class="medium-12 columns">
          <label>
            {{ $t('CRM_MGMT.FORM.COLUMN.TITLE') }}
          </label>
          <draggable v-model="columns" @end="updateColumnIndex">
            <div
              v-for="(column, index) in columns"
              :key="index"
              class="row column-item"
            >
              <i class="ion-android-menu drag-handle" />
              <input
                v-model.trim="column.name"
                type="text"
                :placeholder="
                  $t('CRM_MGMT.FORM.COLUMN_NAME.PLACEHOLDER') +
                    ' ' +
                    (index + 1)
                "
              />

              <div class="column-controls">
                <select
                  v-model="column.campaign_flow"
                  @change="() => { column.campaign_flow_status = null }"
                >
                  <option :value="null">{{ $t('CRM_MGMT.FORM.CAMPAIGN_FLOW.SELECT') }}</option>
                  <option
                    v-for="campaign in campaignsList"
                    :key="campaign.id"
                    :value="campaign"
                  >
                    {{ campaign.title }}
                  </option>
                </select>

                <select
                  v-if="column.campaign_flow"
                  v-model="column.campaign_flow_status"
                >
                  <option :value="null">{{ $t('CRM_MGMT.FORM.CAMPAIGN_FLOW.SELECT_NODE') }}</option>
                  <option
                    v-for="card in getCardsForCampaign(column.campaign_flow)"
                    :key="card.id"
                    :value="card"
                  >
                    {{ getCardLabel(card) }}
                  </option>
                </select>
              </div>
              <woot-button
                v-if="columns.length > 1"
                v-tooltip.top="
                  $t('CRM_MGMT.FORM.COLUMN.REMOVE') + ' ' + (index + 1)
                "
                type="button"
                variant="smooth"
                color-scheme="alert"
                size="tiny"
                icon="dismiss-circle"
                class-names="grey-btn"
                @click.prevent="removeColumn(index)"
              />
            </div>
          </draggable>

          <button class="button small success" type="button" @click="addColumn">
            {{ $t('CRM_MGMT.FORM.COLUMN.ADD') }}
          </button>
        </div>

        <settings-section
          class="settings-select"
          :title="$t('CRM_MGMT.FORM.AGENTS.LABEL')"
          :sub-title="$t('CRM_MGMT.FORM.AGENTS.DESC')"
        >
          <multiselect
            v-model="selectedAgents"
            :options="agentList"
            track-by="id"
            label="name"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :hide-selected="true"
            placeholder="Pick some"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.CLICK_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.CLICK_TO_REMOVE')"
            @select="$v.selectedAgents.$touch"
          />
          <div class="medium-12 columns status restrict-own-tickets">
            <label>
              <input v-model="restrictOwnTickets" type="checkbox" />
              {{ $t('CRM_MGMT.FORM.RESTRICT_OWN_TICKETS.LABEL') }}
            </label>
            <p class="help-text">
              {{ $t('CRM_MGMT.FORM.RESTRICT_OWN_TICKETS.DESC') }}
            </p>
          </div>
        </settings-section>

        <settings-section
          class="settings-select notification-section"
          :title="$t('CRM_MGMT.FORM.NOTIFICATION.TITLE')"
          :sub-title="$t('CRM_MGMT.FORM.NOTIFICATION.DESC')"
        >
          <div class="notification-config">
            <label>
              {{ $t('CRM_MGMT.FORM.NOTIFICATION.INBOX_LABEL') }}
            </label>
            <multiselect
              v-model="selectedInbox"
              :options="inboxList"
              track-by="id"
              label="name"
              :placeholder="$t('CRM_MGMT.FORM.NOTIFICATION.INBOX_PLACEHOLDER')"
              :allow-empty="true"
              :show-labels="false"
              @input="onInboxChange"
            />

            <!-- WhatsApp Template Picker -->
            <div v-if="isWhatsappInbox && selectedInbox" class="template-section">
              <label>{{ $t('CRM_MGMT.FORM.NOTIFICATION.TEMPLATE_LABEL') }}</label>
              <div v-if="selectedTemplate" class="selected-template">
                <div class="template-info">
                  <span class="template-name">{{ selectedTemplate.name }}</span>
                  <div class="template-actions">
                    <woot-button
                      v-if="hasTemplateVariables(selectedTemplate)"
                      type="button"
                      variant="clear"
                      color-scheme="primary"
                      size="tiny"
                      icon="edit"
                      @click.prevent="configureTemplateVariables"
                    >
                      {{ $t('CRM_MGMT.FORM.NOTIFICATION.CONFIGURE_VARIABLES') }}
                    </woot-button>
                    <woot-button
                      type="button"
                      variant="clear"
                      color-scheme="alert"
                      size="tiny"
                      icon="dismiss"
                      @click.prevent="clearTemplate"
                    />
                  </div>
                </div>
                <woot-button
                  v-if="!hasTemplateVariables(selectedTemplate)"
                  type="button"
                  variant="smooth"
                  color-scheme="primary"
                  size="small"
                  icon="document"
                  @click="showTemplatePicker = true"
                >
                  {{ $t('CRM_MGMT.FORM.NOTIFICATION.SELECT_TEMPLATE') }}
                </woot-button>
              </div>
              <woot-button
                v-if="!selectedTemplate"
                type="button"
                variant="smooth"
                color-scheme="primary"
                size="small"
                icon="document"
                @click="showTemplatePicker = true"
              >
                {{ $t('CRM_MGMT.FORM.NOTIFICATION.SELECT_TEMPLATE') }}
              </woot-button>
              <div
                class="variables-help"
                style="margin-top: var(--space-normal); padding-top: var(--space-normal); border-top: 1px solid var(--s-200);"
              >
                <small class="help-text">
                  <strong
                    >{{
                      $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP_TITLE')
                    }}:</strong
                  ><br />
                  <code v-text="'{{ticket_title}}'" /> -
                  {{
                    $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_TITLE')
                  }}<br />
                  <code v-text="'{{ticket_description}}'" /> -
                  {{
                    $t(
                      'CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_DESCRIPTION'
                    )
                  }}<br />
                  <code v-text="'{{contact_name}}'" /> -
                  {{
                    $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.CONTACT_NAME')
                  }}<br />
                  <code v-text="'{{column_name}}'" /> -
                  {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.COLUMN_NAME')
                  }}<br />
                  <code v-text="'{{priority}}'" /> -
                  {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.PRIORITY')
                  }}<br />
                  <code v-text="'{{kanban_name}}'" /> -
                  {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.KANBAN_NAME')
                  }}
                </small>
              </div>
            </div>

            <!-- Regular Message Input -->
            <div v-else-if="selectedInbox" class="message-section">
              <label>{{
                $t('CRM_MGMT.FORM.NOTIFICATION.MESSAGE_LABEL')
              }}</label>
              <textarea
                v-model="notificationMessageText"
                :placeholder="
                  $t('CRM_MGMT.FORM.NOTIFICATION.MESSAGE_PLACEHOLDER')
                "
                rows="4"
                class="message-textarea"
              />
              <div
                class="variables-help"
                style="margin-top: var(--space-normal); padding-top: var(--space-normal); border-top: 1px solid var(--s-200);"
              >
                <small class="help-text">
                  <strong
                    >{{
                      $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP_TITLE')
                    }}:</strong
                  ><br />
                  <code v-text="'{{ticket_title}}'" /> -
                  {{
                    $t(
                      'CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_TITLE'
                    )
                  }}<br />
                  <code v-text="'{{ticket_description}}'" /> -
                  {{
                    $t(
                      'CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_DESCRIPTION'
                    )
                  }}<br />
                  <code v-text="'{{contact_name}}'" /> -
                  {{
                    $t(
                      'CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.CONTACT_NAME'
                    )
                  }}<br />
                  <code v-text="'{{column_name}}'" /> -
                  {{
                    $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.COLUMN_NAME')
                  }}<br />
                  <code v-text="'{{priority}}'" /> -
                  {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.PRIORITY')
                  }}<br />
                  <code v-text="'{{kanban_name}}'" /> -
                  {{
                    $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.KANBAN_NAME')
                  }}
                </small>
              </div>
            </div>
          </div>
        </settings-section>

        <div class="medium-12 columns status">
          <label>
            <input v-model="active" type="checkbox" />
            {{ $t('CRM_MGMT.FORM.ACTIVE.LABEL') }}
          </label>
        </div>

        <div class="medium-12 columns status">
          <label>
            <input v-model="enableSalesFunnel" type="checkbox" />
            {{ $t('CRM_MGMT.FORM.SALES_FUNNEL.LABEL') }}
          </label>
        </div>

        <div class="medium-12 columns status">
          <div class="priority-questions-section">
            <p class="priority-questions-title">
              {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.TITLE') }}
            </p>

            <div class="priority-questions-toggle">
              <label class="priority-questions-toggle-option">
                <input
                  v-model="enablePriorityQuestions"
                  type="radio"
                  :value="true"
                />
                {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.YES') }}
              </label>
              <label class="priority-questions-toggle-option">
                <input
                  v-model="enablePriorityQuestions"
                  type="radio"
                  :value="false"
                />
                {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.NO') }}
              </label>
            </div>

            <div v-if="enablePriorityQuestions" class="priority-questions">
              <p class="help-text" style="margin: var(--space-small) 0 0;">
                {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.DESC') }}
              </p>

              <div
                v-for="(pq, idx) in priorityQuestions"
                :key="idx"
                class="priority-question-row"
              >
                <div class="priority-question-header">
                  <div class="priority-question-field">
                    <label>
                      {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.QUESTION_LABEL') }}
                      <input
                        v-model.trim="pq.question"
                        type="text"
                        :placeholder="$t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.QUESTION_PLACEHOLDER')"
                      />
                    </label>
                  </div>
                  <div class="priority-question-remove">
                    <woot-button
                      v-if="priorityQuestions.length > 1"
                      type="button"
                      variant="smooth"
                      color-scheme="alert"
                      size="tiny"
                      icon="dismiss-circle"
                      class-names="grey-btn"
                      @click.prevent="removePriorityQuestion(idx)"
                    />
                  </div>
                </div>

                <div class="priority-options">
                  <div
                    v-for="(opt, optIdx) in pq.options"
                    :key="optIdx"
                    class="priority-option-row"
                  >
                    <div class="priority-option-field">
                      <label>
                        {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.OPTION_LABEL') }}
                        <input
                          v-model.trim="opt.label"
                          type="text"
                          :placeholder="$t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.OPTION_PLACEHOLDER')"
                        />
                      </label>
                    </div>
                    <div class="priority-option-weight">
                      <label>
                        {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.WEIGHT_LABEL') }}
                        <input
                          v-model.number="opt.weight"
                          type="number"
                          min="0"
                          step="1"
                          :placeholder="$t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.WEIGHT_PLACEHOLDER')"
                        />
                      </label>
                    </div>
                    <div class="priority-option-remove">
                      <woot-button
                        v-if="pq.options.length > 1"
                        type="button"
                        variant="smooth"
                        color-scheme="alert"
                        size="tiny"
                        icon="dismiss-circle"
                        class-names="grey-btn"
                        @click.prevent="removePriorityOption(idx, optIdx)"
                      />
                    </div>
                  </div>

                  <button
                    class="button small priority-add-option"
                    type="button"
                    @click="addPriorityOption(idx)"
                  >
                    {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.ADD_OPTION') }}
                  </button>
                </div>
              </div>

              <button
                class="button small success priority-add-question"
                type="button"
                @click="addPriorityQuestion"
              >
                {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.ADD') }}
              </button>

              <p
                v-if="isPriorityQuestionsWeightSumInvalid"
                class="priority-questions-warning"
                style="margin: var(--space-small) 0 0;"
              >
                {{
                  $t(priorityQuestionsWeightSumWarningKey, {
                    sum: priorityQuestionsWeightSum,
                  })
                }}
              </p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="medium-12 columns">
            <woot-submit-button
              :disabled="$v.$invalid || showLoading || isPriorityQuestionsWeightSumInvalid"
              :button-text="$t('CRM_MGMT.FORM.CREATE')"
              :loading="showLoading"
              class="bg-primary"
            />
            <button class="button clear" @click.prevent="handleCloseWithGuard">
              {{ $t('CRM_MGMT.FORM.CANCEL') }}
            </button>
          </div>
        </div>
      </form>
    </div>
    <woot-modal
      :show.sync="showTemplatePicker"
      :on-close="closeTemplatePicker"
      size="medium"
    >
      <div class="column content-box">
        <woot-modal-header
          :header-title="$t('CRM_MGMT.FORM.NOTIFICATION.SELECT_TEMPLATE')"
        />
        <templates-picker
          v-if="selectedInbox"
          :inbox-id="selectedInbox.id"
          @onSelect="onTemplateSelect"
        />
      </div>
    </woot-modal>

    <!-- Template Parser Modal -->
    <woot-modal
      :show.sync="showTemplateParser"
      :on-close="closeTemplateParser"
      size="medium"
    >
      <div class="column content-box" style="padding: var(--space-normal);">
        <woot-modal-header
          :header-title="$t('CRM_MGMT.FORM.NOTIFICATION.CONFIGURE_TEMPLATE')"
        />
        <template-parser
          v-if="selectedTemplate"
          :template="selectedTemplate"
          :only-select="true"
          @sendMessage="onTemplateParsed"
          @resetTemplate="clearTemplate"
        />
        <div
          class="variables-help"
          style="margin-top: var(--space-normal); padding-top: var(--space-normal); border-top: 1px solid var(--s-200);"
        >
          <small class="help-text">
            <strong
              >{{
                $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP_TITLE')
              }}:</strong
            ><br />
            <code v-text="'{{ticket_title}}'" /> -
            {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_TITLE')
            }}<br />
            <code v-text="'{{ticket_description}}'" /> -
            {{
              $t(
                'CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_DESCRIPTION'
              )
            }}<br />
            <code v-text="'{{contact_name}}'" /> -
            {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.CONTACT_NAME')
            }}<br />
            <code v-text="'{{column_name}}'" /> -
            {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.COLUMN_NAME')
            }}<br />
            <code v-text="'{{priority}}'" /> -
            {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.PRIORITY') }}<br />
            <code v-text="'{{kanban_name}}'" /> -
            {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.KANBAN_NAME') }}
          </small>
        </div>
      </div>
    </woot-modal>

    <!-- Modal de confirmação para dados não salvos -->
    <woot-delete-modal
      :show.sync="showUnsavedChangesConfirm"
      :on-close="cancelUnsavedClose"
      :on-confirm="confirmUnsavedClose"
      :title="$t('CONFIRM_UNSAVED_CHANGES.TITLE')"
      :message="$t('CONFIRM_UNSAVED_CHANGES.MESSAGE')"
      :confirm-text="$t('CONFIRM_UNSAVED_CHANGES.CONFIRM')"
      :reject-text="$t('CONFIRM_UNSAVED_CHANGES.CANCEL')"
    />
  </modal>
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators';
import WootSubmitButton from '../../../../components/buttons/FormSubmitButton';
import Modal from '../../../../components/Modal';
import SettingsSection from '../../../../components/SettingsSection.vue';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import TemplateParser from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplateParser.vue';
import alertMixin from 'shared/mixins/alertMixin';
import unsavedChangesGuard from 'shared/mixins/unsavedChangesGuard';
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import campaignsFlowModule from 'dashboard/store/modules/campaignsFlow';

export default {
  components: {
    WootSubmitButton,
    Modal,
    draggable,
    SettingsSection,
    TemplatesPicker,
    TemplateParser,
  },
  mixins: [alertMixin, unsavedChangesGuard],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      name: '',
      description: '',
          typeOpportunity: 'tickets',
      selectedAgents: [],
      columns: [
        { name: '', index: 0, campaign_flow: null, campaign_flow_status: null },
        { name: '', index: 1, campaign_flow: null, campaign_flow_status: null },
      ],
      active: true,
      enableSalesFunnel: false,
      restrictOwnTickets: false,
      enablePriorityQuestions: false,
      priorityQuestions: [{ question: '', options: [{ label: '', weight: 0 }] }],
      showLoading: false,
      show: true,
      selectedInbox: null,
      selectedTemplate: null,
      notificationMessageText: '',
      showTemplatePicker: false,
      showTemplateParser: false,
      // Unsaved changes confirmation
      showUnsavedChangesConfirm: false,
    };
  },
  validations: {
    name: {
      required,
      minLength: minLength(2),
    },
    description: {},
    columns: {
      $each: {
        name: {
          required,
          minLength: minLength(2),
        },
      },
    },
    selectedAgents: {},
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      inboxList: 'inboxes/getInboxes',
      campaignsList: 'campaignsFlow/getCampaigns',
    }),
    priorityQuestionsWeightSum() {
      if (!this.enablePriorityQuestions) return 0;
      return (this.priorityQuestions || []).reduce((acc, pq) => {
        const options = pq?.options || [];
        const optionsSum = options.reduce((optAcc, opt) => {
          const n = Number(opt?.weight);
          return optAcc + (Number.isFinite(n) ? n : 0);
        }, 0);
        return acc + optionsSum;
      }, 0);
    },
    isPriorityQuestionsWeightSumInvalid() {
      return (
        this.enablePriorityQuestions &&
        this.priorityQuestionsWeightSum !== 100
      );
    },
    priorityQuestionsWeightSumWarningKey() {
      if (!this.enablePriorityQuestions) return '';
      if (this.priorityQuestionsWeightSum > 100) {
        return 'CRM_MGMT.FORM.PRIORITY_QUESTIONS.WEIGHT_SUM_WARNING_OVER';
      }
      return 'CRM_MGMT.FORM.PRIORITY_QUESTIONS.WEIGHT_SUM_WARNING_UNDER';
    },
    isWhatsappInbox() {
      return (
        this.selectedInbox &&
        this.selectedInbox.channel_type === 'Channel::Whatsapp'
      );
    },
    notificationMessage() {
      if (!this.selectedInbox) return {};

      if (this.isWhatsappInbox && this.selectedTemplate) {
        // If template has processed_params (was configured), send full object
        return {
          template: {
            name: this.selectedTemplate.name,
            category: this.selectedTemplate.category,
            language: this.selectedTemplate.language,
            processed_params: this.selectedTemplate.processed_params,
            components: this.selectedTemplate.components,
            media_id: this.selectedTemplate.media_id,
          },
        };
      }
      if (this.notificationMessageText) {
        return { message: this.notificationMessageText };
      }
      return {};
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('campaignsFlow/get');
    // Captura estado inicial após montagem para detectar alterações
    // Usa setTimeout para garantir que todos os dados estejam prontos
    setTimeout(() => {
      this.captureInitialState(this.getFormState());
    }, 100);
  },
  methods: {
    /**
     * Retorna o estado atual do formulário para comparação.
     * Deve incluir todos os campos editáveis.
     */
    getFormState() {
      return {
        name: this.name,
        description: this.description,
        typeOpportunity: this.typeOpportunity,
        columns: this.columns.map(c => ({
          name: c.name,
          campaign_flow_id: c.campaign_flow?.id || null,
          campaign_flow_status_id: c.campaign_flow_status?.id || null,
        })),
        selectedAgents: this.selectedAgents.map(a => a.id).sort(),
        active: this.active,
        enableSalesFunnel: this.enableSalesFunnel,
        restrictOwnTickets: this.restrictOwnTickets,
        enablePriorityQuestions: this.enablePriorityQuestions,
        priorityQuestions: this.priorityQuestions.map(pq => ({
          question: pq.question,
          options: pq.options.map(opt => ({ label: opt.label, weight: opt.weight })),
        })),
        selectedInbox: this.selectedInbox?.id || null,
        notificationMessageText: this.notificationMessageText,
        selectedTemplate: this.selectedTemplate?.name || null,
      };
    },
    /**
     * Intercepta o fechamento do modal para verificar alterações não salvas.
     */
    handleCloseWithGuard() {
      this.handleUnsavedClose(
        this.getFormState(),
        () => this.onClose(),
        () => { this.showUnsavedChangesConfirm = true; }
      );
    },
    getCardsForCampaign(campaignId) {
      if (!this.campaignsList || !campaignId) return [];
      const id = typeof campaignId === 'object' ? campaignId.id : campaignId;
      const campaign = this.campaignsList.find(c => c.id === id);
      return (campaign && campaign.flow && campaign.flow.cards) ? campaign.flow.cards : [];
    },

    getCardLabel(card) {
      return card.label || card.name || card.title || card.id;
    },
    addColumn() {
      this.columns.push({ name: '' });
    },
    removeColumn(index) {
      if (this.columns.length > 1) {
        this.columns.splice(index, 1);
      }
    },
    updateColumnIndex() {
      this.columns.forEach((column, index) => {
        column.index = index;
      });
    },
    async createKanban() {
      const agents_ids = this.selectedAgents.map(agent => agent.id);

      if (this.isPriorityQuestionsWeightSumInvalid) return;

      this.showLoading = true;

      const priority_questions = this.enablePriorityQuestions
        ? (this.priorityQuestions || [])
            .map(pq => ({
              question: (pq.question || '').trim(),
              options: (pq.options || [])
                .map(opt => ({
                  label: (opt.label || '').trim(),
                  weight: Number.isFinite(Number(opt.weight))
                    ? Number(opt.weight)
                    : 0,
                }))
                .filter(opt => opt.label),
            }))
            .filter(pq => pq.question)
        : [];

      const payload = {
        name: this.name,
        agents_ids,
        description: this.description,
        columns: this.columns.map((column, index) => ({
          name: column.name,
          index,
          campaign_flow_id: (column.campaign_flow && column.campaign_flow.id) || column.campaign_flow_id || null,
          campaign_flow_status_id: (column.campaign_flow_status && column.campaign_flow_status.id) || column.campaign_flow_status_id || null,
        })),
        active: this.active,
        enable_sales_funnel: this.enableSalesFunnel,
        restrict_own_tickets: this.restrictOwnTickets,
        type_opportunity: this.typeOpportunity,
        enable_priority_questions: this.enablePriorityQuestions,
        priority_questions,
        inbox_id: this.selectedInbox ? this.selectedInbox.id : null,
        notification_message: this.notificationMessage,
      };

      await this.$store.dispatch('createKanbanfunnel', payload);
      this.showLoading = false;
      this.showAlert(this.$t('CRM_MGMT.ADD.API.SUCCESS_MESSAGE'));
      this.onClose();
    },
    onInboxChange() {
      // Reset notification settings when inbox changes
      this.selectedTemplate = null;
      this.notificationMessageText = '';
    },
    onTemplateSelect(template) {
      this.selectedTemplate = template;
      this.showTemplatePicker = false;
    },
    closeTemplatePicker() {
      this.showTemplatePicker = false;
    },
    closeTemplateParser() {
      this.showTemplateParser = false;
    },
    onTemplateParsed(payload) {
      // Handle parsed template - this will be called when user configures variables
      this.selectedTemplate = {
        ...this.selectedTemplate,
        parsedContent: payload.message,
        processed_params: payload.templateParams.processed_params,
        components: payload.templateParams.components,
      };
      this.showTemplateParser = false;
    },
    hasTemplateVariables(template) {
      if (!template || !template.components) return false;
      const templateString =
        template.components.find(component => component.type === 'BODY')
          ?.text || '';
      const variables = templateString.match(/{{([^}]+)}}/g);
      return variables && variables.length > 0;
    },
    configureTemplateVariables() {
      this.showTemplateParser = true;
    },
    clearTemplate() {
      this.selectedTemplate = null;
      this.showTemplateParser = false;
    },

    addPriorityQuestion() {
      this.priorityQuestions.push({
        question: '',
        options: [{ label: '', weight: 0 }],
      });
    },

    removePriorityQuestion(index) {
      if (this.priorityQuestions.length > 1) {
        this.priorityQuestions.splice(index, 1);
      }
    },

    addPriorityOption(questionIndex) {
      const pq = this.priorityQuestions[questionIndex];
      if (!pq) return;
      if (!pq.options) pq.options = [];
      pq.options.push({ label: '', weight: 0 });
    },

    removePriorityOption(questionIndex, optionIndex) {
      const pq = this.priorityQuestions[questionIndex];
      if (!pq || !pq.options) return;
      if (pq.options.length > 1) {
        pq.options.splice(optionIndex, 1);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.column-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  input {
    flex: 1;
    margin-right: 10px;
    margin-bottom: 0;
  }

  .remove-column-button {
    flex-shrink: 0; /* O botão não vai diminuir de tamanho */
  }
}

.priority-questions-title {
  display: block;
  width: 100%;
  float: none;
  clear: both;
}

.priority-questions-section {
  width: 100%;
  display: flex;
  flex-direction: column;
}
.status {
  margin-top: 1rem;
}

.restrict-own-tickets {
  margin-top: var(--space-normal);
  padding-left: 0;

  .help-text {
    margin: var(--space-micro) 0 0;
    color: var(--s-600);
    font-size: var(--font-size-mini);
  }
}

.priority-questions {
  margin-top: var(--space-small);
  float: none;
  clear: both;
  width: 100%;

  display: flex;
  flex-direction: column;

  .priority-question-row {
    margin-top: var(--space-small);
  }

  .help-text {
    color: var(--s-600);
  }
}

.priority-questions-toggle {
  margin-top: var(--space-small);
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  float: none;
  clear: both;
  width: 100%;
}

.priority-question-row {
  width: 100%;
}

.priority-question-header {
  display: flex;
  gap: var(--space-small);
  align-items: flex-end;
  width: 100%;
}

.priority-question-field {
  flex: 1;
}

.priority-question-remove {
  width: 40px;
  display: flex;
  justify-content: flex-end;
}

.priority-options {
  margin-top: var(--space-small);
  display: flex;
  flex-direction: column;
  gap: var(--space-small);

  margin-left: var(--space-normal);
  padding-left: var(--space-normal);
  border-left: 2px solid var(--s-100);
}

.priority-option-row {
  display: flex;
  gap: var(--space-small);
  align-items: flex-end;
  width: 100%;
}

.priority-option-row label {
  font-size: var(--font-size-small);
  color: var(--s-700);
}

.priority-option-row input {
  padding: 6px 10px;
  font-size: var(--font-size-small);
}

.priority-option-field {
  flex: 1;
}

.priority-option-weight {
  width: 120px;
}

.priority-option-remove {
  width: 40px;
  display: flex;
  justify-content: flex-end;
}

.priority-add-option {
  align-self: flex-start;
  padding: 6px 10px;
  font-size: var(--font-size-small);
  background: transparent;
  border: 1px solid var(--s-200);
  color: var(--s-700);
}

.priority-add-question {
  width: fit-content;
  align-self: flex-end;
  margin: var(--space-small) 0 0 auto;
}

.priority-questions-toggle-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.priority-questions-warning {
  color: var(--w-700);
  font-weight: var(--font-weight-medium);
}

.modal-container .modal-footer .button {
  margin-left: 0;
}

.drag-handle {
  cursor: grab;
  margin-right: 10px;
  font-size: 20px;
}

.settings-select {
  width: 100%;
}

.notification-section {
  margin-top: var(--space-normal);
  border-top: 1px solid var(--s-100);
  padding-top: var(--space-normal);
}

.notification-config {
  width: 100%;

  label {
    margin-bottom: var(--space-small);
    font-weight: var(--font-weight-medium);
  }
}

.template-section,
.message-section {
  margin-top: var(--space-normal);
}

.selected-template {
  background: var(--s-50);
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-normal);
  padding: var(--space-small);

  .template-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .template-name {
    font-weight: var(--font-weight-medium);
    color: var(--s-700);
  }

  .template-actions {
    display: flex;
    gap: var(--space-small);
    align-items: center;
  }
}

.message-textarea {
  width: 100%;
  resize: vertical;
  min-height: 100px;
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-normal);
  padding: var(--space-small);
}

.variables-help {
  margin-top: var(--space-small);

  .help-text {
    color: var(--s-600);
    line-height: 1.4;

    strong {
      color: var(--s-700);
    }
  }
}

.column-controls {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
  margin-right: 10px;
}

.column-controls select {
  min-width: 180px;
  flex: 1;
  margin-bottom: 0;
  padding: 8px 28px 8px 12px;
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-normal);
  background-color: var(--white);
  font-size: var(--font-size-small);
  cursor: pointer;

  &:focus {
    border-color: var(--w-500);
    outline: none;
  }
}
</style>
